/* Redux */
import { AnyAction, Dispatch } from 'redux';
import { tipsActions } from '../../../store/tips/actions';
import { premiumInfoActions } from '../../../store/premium/actions';

/* Interfaces */
import { Payload, TipPurchasePayload } from './Payload';
import { AnalyticsClient } from '../../../analytics/Client';
import { StoreType, StoreTypesReversed } from './store_types';
import {
  PaymentError,
  PaymentProcessor,
  WithPayload,
} from './PaymentProcessor';
import { PurchaseStatus } from '../../Shops/shared/__providers__/PurchaseStatusProvider';
import { Shop } from '../../Shops/shared/interfaces';
import { TipProduct } from '../../Shops/Tip/interfaces/TipProduct';
import { Price } from './Price';
import { PremiumProduct } from '../../Shops/Premium/interfaces/PremiumProduct';

/* Services */
import { requestPremiumPaymentIntent } from '../../../api/payments/Stripe/premiumPayments';
import { requestTipPaymentIntent } from '../../../api/payments/Stripe/tipPayments';

/* Toast */
import { toast } from 'react-toastify';

/* Translations */
import i18n from '../../../i18n';

/* Utils */
import { delay } from '../utils/delay';

export class StripePaymentProcessor
  extends PaymentProcessor
  implements WithPayload
{
  protected handler: any;
  protected options: any;
  public payload: Payload;
  readonly product: Shop;

  constructor(
    payload: Payload,
    product: Shop,
    dispatcher: Dispatch<AnyAction>,
    loader: (load: boolean) => void,
    analyticsClient: AnalyticsClient,
    setPurchaseStatus: (status: PurchaseStatus) => void,
    handler: any,
    options: any,
  ) {
    super(dispatcher, loader, analyticsClient, setPurchaseStatus);

    this.payload = payload;
    this.product = product;
    this.handler = handler;
    this.options = options;
  }

  public async processPayment(store: StoreType): Promise<any> {
    this.loader(true);

    try {
      switch (store) {
        case StoreTypesReversed.Premium:
          const {
            premium_plan_purchase: {
              user_id: premiumTipsterID,
              client_secret: premiumClientSecret,
            },
          } = await requestPremiumPaymentIntent(this.payload);

          const { paymentIntent: premiumPaymentIntent, error: premiumError } =
            await this.handler.confirmCardPayment(
              premiumClientSecret,
              this.options,
            );

          if (premiumError) {
            return this.handleError(premiumError as PaymentError);
          }

          this.finishPayment(store, premiumPaymentIntent, premiumTipsterID);
          break;

        case StoreTypesReversed.TipPurchase:
          const {
            purchase: {
              // user_id: tipTipsterID,
              payment_provider: { client_secret: tipClientSecret },
            },
          } = await requestTipPaymentIntent(this.payload);

          const { paymentIntent: tipPaymentIntent, error: tipError } =
            await this.handler.confirmCardPayment(
              tipClientSecret,
              this.options,
            );

          if (tipError) {
            return this.handleError(tipError as PaymentError);
          }

          this.finishPayment(
            store,
            tipPaymentIntent,
            (this.payload as TipPurchasePayload).tipID,
          );
          break;
      }
    } catch (error: unknown) {
      this.handleError(error as PaymentError);
    }
  }

  public async finishPayment(
    store: StoreType,
    paymentIntent?: any,
    productID?: string,
  ): Promise<any> {
    toast.success(`${i18n.t('translation:payment_success')}`);
    this.setPurchaseStatus(PurchaseStatus.COMPLETED);
    const tipsterInfo = this.product.tipsterInfo;

    if (paymentIntent && productID) {
      await delay(1500);

      this.loader(false);

      switch (store) {
        case StoreTypesReversed.Premium:
          const premiumProduct = this.product.selectedProduct as PremiumProduct;

          this.analyticsClient.firebase.log('purchase', [
            {
              transaction_id: productID || '',
              store: 'Stripe',
              type: 'premium',
              currency: premiumProduct.priceWithDiscount.currency,
              value: premiumProduct.priceWithDiscount.buildPrice().toNumber(),
              items: [
                {
                  item_id: premiumProduct.plan.pricing_tier_code,
                  item_name: `Premium Tier ${premiumProduct.getPricingTierCodeNumber()}`,
                  item_list_name: 'Premium',
                },
              ],
            },
          ]);

          this.dispatcher(
            premiumInfoActions.publicPremiumInfoRequestAction(tipsterInfo.id),
          );
          break;

        case StoreTypesReversed.TipPurchase:
          const tipProduct = this.product.selectedProduct as TipProduct;

          this.analyticsClient.firebase.log('purchase', [
            {
              transaction_id: productID || '',
              store: 'Stripe',
              type: 'tip_coupon',
              currency: tipProduct.tip.web_price.currency,
              value: new Price(
                tipProduct.tip.web_price_with_discount.amount,
                tipProduct.tip.web_price_with_discount.currency,
                tipProduct.tip.web_price_with_discount.decimals,
              )
                .buildPrice()
                .toNumber(),
              items: [
                {
                  item_id: tipProduct.tip.pricing_tier_code,
                  item_name: tipProduct.tip.pricing_tier_code,
                  item_list_name: 'Tip',
                },
              ],
            },
          ]);

          this.dispatcher(
            tipsActions.tipProductRequestAction(tipProduct.tip.id),
          );
          break;
      }
    }
  }

  public handleError(error: PaymentError) {
    toast.error(i18n.t(error.detail || error.message));

    this.setPurchaseStatus(PurchaseStatus.FAILED);
    this.loader(false);
  }
}
